import React from "react";
import { useTranslate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { theme } from "antd";
import { GlassCard } from "../../../shared/components";
import type { usePageStyles } from "../../../shared/styles";
import { getCountryName } from "../../../utils/countries";

type PageStyles = ReturnType<typeof usePageStyles>;

type AccountOverviewContentProps = {
  record: any;
  styles: PageStyles;
};

/** Build a flag emoji from a 2-letter ISO country code (regional indicators). */
const countryFlag = (code?: string): string => {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) {
    return "🏳️";
  }
  return code
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65),
    );
};

export const AccountOverviewContent: React.FC<AccountOverviewContentProps> = ({ record, styles }) => {
  const _t = useTranslate();
  const { i18n } = useTranslation();
  const { token } = theme.useToken();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
      {/* Contact Card */}
      <GlassCard
        title={_t("accounts.sections.contact")}
        variant="indigo"
        icon={
          <svg width="20" height="20" fill="none" stroke={token.colorPrimary} strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
      >
        <div className="flex flex-col gap-5" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <div style={styles.label}>{_t("accounts.fields.phone")}</div>
            {record?.phone ? (
              <a href={`tel:${record?.phone}`} style={styles.phoneLink}>
                {record?.phone}
                <span style={styles.callBadge}>{_t("accounts.actions.call")}</span>
              </a>
            ) : (
              <span style={styles.emptyText}>-</span>
            )}
          </div>
          <div>
            <div style={styles.label}>{_t("accounts.fields.email")}</div>
            {record?.email ? (
              <a href={`mailto:${record?.email}`} style={{
                color: token.colorPrimary,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: 500
              }}>
                {record?.email}
              </a>
            ) : (
              <span style={styles.emptyText}>-</span>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Address Card */}
      <GlassCard
        title={_t("accounts.sections.address")}
        variant="pink"
        icon={
          <svg width="20" height="20" fill="none" stroke="#f472b6" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
      >
        <div style={styles.addressText}>
          {record?.address && <div style={{ fontWeight: 500 }}>{record?.address}</div>}
          {(record?.zipCode || record?.city) && (
            <div style={{ marginTop: '8px' }}>
              {record?.zipCode} {record?.city}
            </div>
          )}
          {record?.country && (
            <div style={styles.addressMuted}>
              <span style={{ fontSize: '14px' }}>{countryFlag(record?.country)}</span>
              {getCountryName(record?.country, i18n.language)}
            </div>
          )}
          {!record?.address && !record?.zipCode && !record?.city && !record?.country && (
            <span style={styles.emptyText}>-</span>
          )}
        </div>
      </GlassCard>

      {/* Billing Card */}
      <GlassCard
        title={_t("accounts.sections.billing")}
        variant="emerald"
        icon={
          <svg width="20" height="20" fill="none" stroke="#34d399" strokeWidth="1.5" viewBox="0 0 24 24">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="1" y1="10" x2="23" y2="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
      >
        <div className="flex flex-col gap-4" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={styles.infoRow}>
            <span style={styles.infoRowLabel}>{_t("accounts.fields.billingMode")}</span>
            <span style={styles.billingBadge}>{_t(`accounts.fields.billingModeValues.${record?.billingMode}`)}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoRowLabel}>{_t("accounts.fields.credit")}</span>
            <span style={styles.infoRowValue}>{record?.credit ?? 0}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoRowLabel}>{_t("accounts.fields.applyVat")}</span>
            <span style={styles.checkBox(record?.applyVat)}>
              {record?.applyVat && (
                <svg width="12" height="12" fill="none" stroke="#34d399" strokeWidth="2" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};
